'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProviderProfile } from '@morfi/backend-sdk-v2/client';
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { getProviderDisplayName } from '@/components/settings/provider-display-name';
import { ProviderIcon } from '@/components/settings/provider-icon';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedSwitch } from '@/components/ui/animated-switch';
import { Icon } from '@/components/ui/icon';
import { CheckUnread01Icon, Tick01IconSolid } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { normalizeHandleCandidate, useSuggestedHandle } from '@/hooks/profile/use-suggested-handle';
import { cn } from '@/lib/ui/utils';
interface ConnectProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: ProviderProfile | null;
  onConnect: (importProfile: boolean, importHandle: boolean, suggestedHandle: string | null) => void;
  isPending: boolean;
}
type HandleCheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'already-matches';
export function ConnectProviderDialog(t0) {
  const $ = _c(79);
  const {
    open,
    onOpenChange,
    profile,
    onConnect,
    isPending
  } = t0;
  const t = useTranslations("settings.connectedAccounts.connectDialog");
  const {
    user
  } = useAuth();
  const [importProfile, setImportProfile] = useState(true);
  const [importHandle, setImportHandle] = useState(true);
  let t1;
  if ($[0] !== profile) {
    t1 = profile !== null && profile.provider === "x" ? normalizeHandleCandidate(profile.username) ?? "" : "";
    $[0] = profile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const xHandle = t1;
  const alreadyMatches = xHandle !== "" && user?.handle !== null && user?.handle !== undefined && user.handle === xHandle;
  const suggested = useSuggestedHandle(open && !alreadyMatches && xHandle !== "" ? xHandle : null);
  let handleCheckStatus;
  let suggestedHandle;
  if ($[2] !== alreadyMatches || $[3] !== suggested || $[4] !== xHandle) {
    if (xHandle === "") {
      handleCheckStatus = "idle";
      suggestedHandle = null;
    } else {
      if (alreadyMatches) {
        handleCheckStatus = "already-matches";
        suggestedHandle = null;
      } else {
        if (suggested.status === "ready") {
          handleCheckStatus = suggested.primaryTaken ? "taken" : "available";
          suggestedHandle = suggested.suggestion;
        } else {
          if (suggested.status === "exhausted") {
            handleCheckStatus = "taken";
            suggestedHandle = null;
          } else {
            handleCheckStatus = "checking";
            suggestedHandle = null;
          }
        }
      }
    }
    $[2] = alreadyMatches;
    $[3] = suggested;
    $[4] = xHandle;
    $[5] = suggestedHandle;
    $[6] = handleCheckStatus;
  } else {
    suggestedHandle = $[5];
    handleCheckStatus = $[6];
  }
  if (profile === null) {
    return null;
  }
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== handleCheckStatus || $[8] !== importHandle || $[9] !== importProfile || $[10] !== isPending || $[11] !== onConnect || $[12] !== onOpenChange || $[13] !== open || $[14] !== profile.avatarUrl || $[15] !== profile.displayName || $[16] !== profile.email || $[17] !== profile.provider || $[18] !== profile.username || $[19] !== suggestedHandle || $[20] !== t) {
    const providerName = getProviderDisplayName(profile.provider);
    const identifier = profile.username !== null ? `@${profile.username}` : profile.email;
    const showProfileSwitch = profile.displayName !== null || profile.avatarUrl !== null;
    const showHandleSwitch = profile.provider === "x" && profile.username !== null;
    const handleIsAvailable = handleCheckStatus === "available" || handleCheckStatus === "already-matches";
    const handleIsUnavailable = handleCheckStatus === "taken";
    const handleStatusTone = handleIsAvailable ? "text-feedback-success-content" : handleIsUnavailable ? "text-feedback-error-content" : "text-foreground-secondary";
    const handleSwitchDisabled = isPending || handleCheckStatus === "already-matches" || handleCheckStatus === "checking";
    T0 = SilkSheet;
    t7 = open;
    t8 = onOpenChange;
    t9 = "detached";
    t10 = "md";
    t11 = true;
    t12 = t("title", {
      provider: providerName
    });
    if ($[35] !== t) {
      t13 = t("description");
      $[35] = t;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    let t15;
    if ($[37] !== onOpenChange) {
      t15 = () => onOpenChange(false);
      $[37] = onOpenChange;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[39] !== t) {
      t16 = t("cancel");
      $[39] = t;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== isPending || $[42] !== t15 || $[43] !== t16) {
      t17 = <AnimatedButton variant="secondary" onClick={t15} disabled={isPending}>{t16}</AnimatedButton>;
      $[41] = isPending;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== importHandle || $[46] !== importProfile || $[47] !== onConnect || $[48] !== suggestedHandle) {
      t18 = () => onConnect(importProfile, importHandle, suggestedHandle);
      $[45] = importHandle;
      $[46] = importProfile;
      $[47] = onConnect;
      $[48] = suggestedHandle;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    let t19;
    if ($[50] !== isPending || $[51] !== t) {
      t19 = isPending ? t("connecting") : t("confirm");
      $[50] = isPending;
      $[51] = t;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    let t20;
    if ($[53] !== isPending || $[54] !== t18 || $[55] !== t19) {
      t20 = <AnimatedButton onClick={t18} disabled={isPending} loading={isPending}>{t19}</AnimatedButton>;
      $[53] = isPending;
      $[54] = t18;
      $[55] = t19;
      $[56] = t20;
    } else {
      t20 = $[56];
    }
    if ($[57] !== t17 || $[58] !== t20) {
      t14 = <ModalFooter>{t17}{t20}</ModalFooter>;
      $[57] = t17;
      $[58] = t20;
      $[59] = t14;
    } else {
      t14 = $[59];
    }
    t5 = "flex flex-col gap-4 pb-4";
    t6 = (profile.avatarUrl !== null || identifier !== null || profile.displayName !== null) && <div className="flex items-center gap-3 rounded-control border border-border-primary p-3">{profile.avatarUrl !== null ? <OptimizedImage src={profile.avatarUrl} alt={profile.displayName ?? providerName} variant="avatar" nsfw={false} size={40} className="size-10" /> : <div className="flex size-10 items-center justify-center rounded-full bg-background-tertiary"><ProviderIcon provider={profile.provider} className="size-5 text-foreground-secondary" /></div>}<div className="min-w-0 flex-1">{identifier !== null && <p className="truncate text-body font-medium text-foreground-primary">{identifier}</p>}{profile.displayName !== null && <p className="truncate text-body text-foreground-secondary">{profile.displayName}</p>}</div></div>;
    t2 = "flex flex-col gap-2";
    t3 = showProfileSwitch && <div className="flex items-center justify-between gap-3 p-2"><span className="text-caption font-medium text-foreground-primary">{t("importProfile", {
          provider: providerName
        })}</span><AnimatedSwitch checked={importProfile} onCheckedChange={setImportProfile} disabled={isPending} aria-label={t("importProfile", {
        provider: providerName
      })} /></div>;
    t4 = showHandleSwitch && profile.username !== null && <div className="flex items-start justify-between gap-3 p-2"><div className="flex min-w-0 flex-col gap-0.5"><span className="text-caption font-medium text-foreground-primary">{t("importHandle", {
            provider: providerName
          })}</span><span className={cn("flex items-center gap-1 text-micro", handleStatusTone)}>{handleIsAvailable && <Icon icon={Tick01IconSolid} className="size-2.5 shrink-0" />}{handleIsUnavailable && <Icon icon={CheckUnread01Icon} className="size-2.5 shrink-0" />}{handleIsUnavailable ? t("handleUnavailable", {
            handle: profile.username
          }) : t("handleAvailable", {
            handle: profile.username
          })}</span></div><AnimatedSwitch checked={importHandle && handleCheckStatus !== "already-matches"} onCheckedChange={setImportHandle} disabled={handleSwitchDisabled} aria-label={t("importHandle", {
        provider: providerName
      })} /></div>;
    $[7] = handleCheckStatus;
    $[8] = importHandle;
    $[9] = importProfile;
    $[10] = isPending;
    $[11] = onConnect;
    $[12] = onOpenChange;
    $[13] = open;
    $[14] = profile.avatarUrl;
    $[15] = profile.displayName;
    $[16] = profile.email;
    $[17] = profile.provider;
    $[18] = profile.username;
    $[19] = suggestedHandle;
    $[20] = t;
    $[21] = T0;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    T0 = $[21];
    t10 = $[22];
    t11 = $[23];
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t2 = $[27];
    t3 = $[28];
    t4 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t15;
  if ($[60] !== t2 || $[61] !== t3 || $[62] !== t4) {
    t15 = <div className={t2}>{t3}{t4}</div>;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t15 || $[65] !== t5 || $[66] !== t6) {
    t16 = <div className={t5}>{t6}{t15}</div>;
    $[64] = t15;
    $[65] = t5;
    $[66] = t6;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  let t17;
  if ($[68] !== T0 || $[69] !== t10 || $[70] !== t11 || $[71] !== t12 || $[72] !== t13 || $[73] !== t14 || $[74] !== t16 || $[75] !== t7 || $[76] !== t8 || $[77] !== t9) {
    t17 = <T0 open={t7} onOpenChange={t8} variant={t9} size={t10} keyboard={t11} title={t12} description={t13} footer={t14}>{t16}</T0>;
    $[68] = T0;
    $[69] = t10;
    $[70] = t11;
    $[71] = t12;
    $[72] = t13;
    $[73] = t14;
    $[74] = t16;
    $[75] = t7;
    $[76] = t8;
    $[77] = t9;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  return t17;
}